import React, { useState, useEffect, useRef, useMemo } from "react";
import { httpDelete, useHttpGets } from "../../../services/httpService";
import { REIButton, REIDialoge, REIProgressSpinner } from "../../../components/ui";
import REITab from "../../../components/ui/tab";
import { ITab } from "../../../interface/tab";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY, Messages, USER_DETAIL } from "../../../utils/constants";
import EntityDetails from "./entityDetails";
import { IMyEntityAndGoals } from "../../../interface/myEntityAndGoals";
import EntityOwnerShip from "../../ProppertyAnalyser/PropertyStepper/Child/AddEntityOwnership";
import { IEntityType } from "../../../interface/analysed";
import { useLocation, useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import { fetchMemberEntities } from "../../../store/slice/myTrackerSlice";
import { AppDispatch } from "../../../store";
import { useDispatch } from "react-redux";

const MyEntityGoalsIndex: React.FC = () => {
  const { selectedEntityId, isShowingTotals, setSelectedEntityId } = useFilterSelection();
  const memberId = localStorage.getItem(MEMBER_KEY);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [refreshData, setRefreshData] = useState(false);
  const [entityData, setEntityData] = useState<IMyEntityAndGoals[]>([]);
  const [showEntityOwnershipDialog, setShowEntityOwnershipDialog] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [viewOption, setViewOption] = useState<"totals" | "portion">(isShowingTotals ? "totals" : "portion");
  const [showDeleteEntityDialog, setShowDeleteEntityDialog] = useState(false);
  const toastRef = useRef<any>(null);
  const memberEntities = useRef<IEntityType[]>([]);
  const dispatch: AppDispatch = useDispatch();

  const hideEntityOwnershipDialog = () => {
    setRefreshData((prev) => !prev);
    setShowEntityOwnershipDialog(false);
  };

  useEffect(() => {
    const fetchAndSetTabs = async (memberEntities: IEntityType[]) => {
      try {
        setIsLoading(true);
        const response = await useHttpGets(
          `${ApiEndPoints.tracker}EntityAndGoals?MemberId=${memberId}&userCountryCode=${memberDetail.Country}&memberName= ${memberDetail.FirstName} ${memberDetail.LastName}`
        );
        if (response.status === 200 && Array.isArray(response.data)) {
          setEntityData(
            (response.data as IMyEntityAndGoals[]).map((entity: IMyEntityAndGoals) => ({
              ...entity,
              EntityTypeName: memberEntities.find((data: IEntityType) => data.EntityTypeCode === entity.EntityTypeCode)
                ?.EntityTypeLabel,
            }))
          );
          findAndSetActiveTab(response.data);
        } else {
          setEntityData([]);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Error fetching and setting tabs data:", error);
      }
    };

    const fetchEntityData = async () => {
      try {
        await useHttpGets(`${ApiEndPoints.tracker}/GetEntityTypes/AU`, undefined)
          .then((result) => {
            if (result?.data != null && !result.error) {
              if (Array.isArray(result.data)) {
                memberEntities.current = result.data;
                fetchAndSetTabs(result.data);
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching entity data:", error);
          });
      } catch (error) {
        console.error("Error fetching entity data:", error);
      }
    };
    fetchEntityData();
  }, [refreshData]);

  useEffect(() => {
    if (location.state?.entityId) {
      navigate("/portfolio-tracker", { replace: true });
    }
  }, [location.state, navigate]);

  const findAndSetActiveTab = (entityData: IMyEntityAndGoals[]) => {
    let foundEntityIndex = -1;

    if (location.state?.entityId) {
      foundEntityIndex = entityData.findIndex(
        (entity: IMyEntityAndGoals) => entity.EntityId === location.state.entityId
      );

      if (location.state) {
        navigate("/portfolio-tracker", {
          replace: true,
          state: { ...location.state, entityId: undefined },
        });
      }
    }

    if (foundEntityIndex === -1 && selectedEntityId) {
      foundEntityIndex = entityData.findIndex(
        (entity: IMyEntityAndGoals) => entity.EntityId === selectedEntityId.toString()
      );
    }

    if (foundEntityIndex !== -1) {
      setActiveTabIndex(foundEntityIndex);
    } else if (entityData.length > 0) {
      setActiveTabIndex(0);
    }
  };

  const renderHeader = (entity: IMyEntityAndGoals) => {
    if (!entity) return null;
    return (
      <>
        <div className="flex flex-col">
          <div className="font-bold text-5xl text-left mb-3 flex justify-between">
            <span>{entity.EntityName}</span>
            {entity?.EntityId != "" && entity?.EntityName != "Personal Ownership" && (
              <REIButton
                id={`megidx-btn-deleteentity-${entity.EntityId}`}
                classNames="text-gray-200 bg-gray-400 border-none"
                onClick={() => {
                  setShowDeleteEntityDialog(true);
                }}
                label="Delete this entity"
              ></REIButton>
            )}
          </div>
          <div className="flex flex-row justify-between items-center mb-6">
            <div className="flex flex-row content-center items-center gap-2">
              <div className="field-radiobutton mr-2">
                <RadioButton
                  className="mx-2"
                  inputId="totals"
                  data-testid={`megidx-rdo-totals-${entity.EntityId}`}
                  name="viewOption"
                  value="totals"
                  onChange={(e) => setViewOption(e.value)}
                  checked={viewOption === "totals"}
                />
                <label htmlFor="totals" className="ml-2 align-middle">
                  Show totals
                </label>
              </div>
              <div className="field-radiobutton">
                <RadioButton
                  inputId="portion"
                  data-testid={`megidx-rdo-portion-${entity.EntityId}`}
                  name="viewOption"
                  value="portion"
                  onChange={(e) => setViewOption(e.value)}
                  checked={viewOption === "portion"}
                />
                <label htmlFor="portion" className="ml-2 align-middle">
                  Show just this entity's portion
                </label>
              </div>
            </div>
            <div className="flex self-end items-end italic text-sky-300">
              To add goals, notes, or change targets, simply click into a field and start typing.
            </div>
          </div>
          {entity.EntityId != "" && (
            <div className="flex justify-around border-solid border border-gray-200 p-4 w-full mb-6">
              <div className="text-xl">
                <b>Entity type:</b> {entity.EntityTypeName}
              </div>
              <div className="text-xl">
                <b>Entity country:</b> {entity.CountryCode == "AU" ? "Australia" : "New Zealand"}
              </div>
            </div>
          )}
          {entity.Properties.length == 0 && (
            <div className="flex mb-6">
              <span>
                You currently don't have any properties in this entity. You can select an existing property from another
                entity to add to it, or you can add a new property to your portfolio and assign it here.
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  const tabs = useMemo<ITab[]>(() => {
    if (!entityData || entityData.length === 0) return [];
    return entityData.map((entity) => ({
      header: entity.EntityName,
      content: (
        <>
          {renderHeader(entity)}
          <EntityDetails
            entity={{
              ...entity,
              EntityTypeName: memberEntities.current.find((data) => data.EntityTypeCode == entity.EntityTypeCode)
                ?.EntityTypeLabel,
            }}
            handleRefreshData={() => setRefreshData((p) => !p)}
            viewOption={viewOption}
          />
        </>
      ),
      closable: false,
    }));
  }, [entityData, viewOption]);

  const handleDeleteEntity = async () => {
    setIsLoading(true);
    await httpDelete(
      `${ApiEndPoints.tracker}MemberEntity?memberId=${memberId}&memberEntityId=${entityData[activeTabIndex].EntityId}`,
      undefined
    ).then((result) => {
      if (result.status == 200) {
        toastRef.current?.show({
          severity: "success",
          summary: Messages.DELETE_SUCCESS,
          detail: "Entity Deleted Successfully",
          life: 2000,
        });
        setRefreshData((prev) => !prev);
        setShowDeleteEntityDialog(false);
        setIsLoading(false);
        selectedEntityId.toString() === entityData[activeTabIndex].EntityId && setSelectedEntityId(0);
        dispatch(fetchMemberEntities());
      } else {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.DELETE_ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setIsLoading(false);
      }
    });
  };

  const hideDeleteEntityDialog = () => {
    setShowDeleteEntityDialog(false);
  };

  return isLoading ? (
    <div className="content-center flex justify-center items-center min-h-[100px]">
      <REIProgressSpinner />
    </div>
  ) : (
    <>
      <Toast ref={toastRef} />
      <REIDialoge
        showHeader={true}
        header="Are you sure you want to delete this entity?"
        visible={showDeleteEntityDialog}
        handleEditClick={() => {
          hideDeleteEntityDialog();
        }}
        position="center"
        style={{ width: "60vw" }}
        customClass="edit-default-profile"
        contentComponent={
          <div className="flex flex-col items-start p-4">
            <p>
              We recommend before you delete this entity that you re-assign any properties within it to a new entity. If
              you delete without doing so, then all properties within it will be placed in your 'default' entity.
            </p>
            <div className="flex flex-row gap-8 items-center">
              <REIButton
                id="megidx-btn-confirmdelete"
                label="Delete this entity"
                onClick={() => {
                  handleDeleteEntity();
                }}
              ></REIButton>
              <a
                id="megidx-lnk-closedelete"
                data-testid="megidx-lnk-closedelete"
                className="text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => {
                  hideDeleteEntityDialog();
                }}
              >
                Close this window and do not delete
              </a>
            </div>
          </div>
        }
      ></REIDialoge>

      <REIDialoge
        showHeader={true}
        header={`Add a new entity ownership structure`}
        handleEditClick={hideEntityOwnershipDialog}
        position="top"
        customClass="update-statistics-dialog"
        style={{ width: "55vw" }}
        contentComponent={<EntityOwnerShip closeDialogue={hideEntityOwnershipDialog} />}
        visible={showEntityOwnershipDialog}
      ></REIDialoge>

      <div className="flex flex-row justify-between gap-4">
        <div className="flex flex-col content-center items-start">
          <h1>Portfolio Tracker — My Entities & Goals</h1>
          <p>Manage your entities and your goals to keep track of how you’re performing against your targets.</p>
        </div>
        <REIButton
          id="megidx-btn-addentity"
          onClick={() => setShowEntityOwnershipDialog(true)}
          label="Add new entity"
          classNames="h-12"
        ></REIButton>
      </div>

      <div className=" mx-auto p-4 ">
        <REITab
          id="megidx-tab-entities"
          tabs={tabs}
          classNames="text-center"
          activeIndex={activeTabIndex}
          onTabChange={(e) => {
            setActiveTabIndex(e.index);
            setSelectedEntityId(Number(entityData[e.index]?.EntityId) || 0);
          }}
        ></REITab>
      </div>
    </>
  );
};

export default MyEntityGoalsIndex;
